import { useState } from 'react'
import { Monitor, Terminal, LogOut, ChevronRight, Users, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useActiveSessions, useTerminateSession } from '../../hooks/useActiveSessions'
import SessionDetailModal from '../../components/Modal/SessionDetailModal'

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

export default function ActiveSessions() {
  const { t } = useTranslation()
  const { data: sessions = [], isLoading, refetch, isRefetching } = useActiveSessions()
  const { mutate: terminate, isPending: isTerminating, variables: terminatingId } = useTerminateSession()
  const [selected, setSelected] = useState(null)

  const handleTerminate = (sessionId) => {
    terminate(sessionId, { onSuccess: () => setSelected(null) })
  }

  return (
    <>
      <div className="card p-6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.activeSessions.title')}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {sessions.length > 0
                ? t('dashboard.activeSessions.inProgress', { count: sessions.length })
                : t('dashboard.activeSessions.emptyHint')}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('dashboard.activeSessions.refresh')}
          >
            <RefreshCw className={clsx('w-4 h-4', isRefetching && 'animate-spin')} />
          </button>
        </div>

        {/* List */}
        <div className="flex-1">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-10">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t('dashboard.activeSessions.emptyList')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((session) => {
                const isRemote = session.sessionType === 'RemoteDisplay'
                const Icon = isRemote ? Monitor : Terminal
                const isBeingTerminated = isTerminating && terminatingId === session.sessionId

                return (
                  <div
                    key={session.sessionId ?? `${session.hostId}-ssh`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
                  >
                    {/* Tipo */}
                    <div className={clsx(
                      'p-2 rounded-lg shrink-0',
                      isRemote ? 'bg-blue-100' : 'bg-green-100'
                    )}>
                      <Icon className={clsx('w-4 h-4', isRemote ? 'text-blue-600' : 'text-green-600')} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {session.hostName}
                        </span>
                        <span className={clsx(
                          'shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium',
                          isRemote ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        )}>
                          {isRemote ? t('dashboard.activeSessions.remote') : t('dashboard.activeSessions.ssh')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 truncate">
                        {session.userName && <span>{session.userName}</span>}
                        {session.userName && <span>·</span>}
                        <span>{formatDuration(session.durationSeconds)}</span>
                        {session.latencyMs != null && (
                          <>
                            <span>·</span>
                            <span>{Math.round(session.latencyMs)} ms</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-1 shrink-0">
                      {session.canTerminate && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleTerminate(session.sessionId) }}
                          disabled={isBeingTerminated}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          title={t('dashboard.activeSessions.terminate')}
                        >
                          <LogOut className={clsx('w-3.5 h-3.5', isBeingTerminated && 'animate-spin')} />
                        </button>
                      )}
                      <button
                        onClick={() => setSelected(session)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                        title={t('dashboard.activeSessions.viewDetails')}
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <SessionDetailModal
          session={selected}
          onClose={() => setSelected(null)}
          onTerminate={handleTerminate}
          isTerminating={isTerminating && terminatingId === selected.sessionId}
        />
      )}
    </>
  )
}
